<template>
  <form style="margin-bottom: 40px" @submit.prevent="submit">
    <div class="two-form-items two-form-items_narrow">
      <AddProjectFormInput
        id="project-name"
        v-model="name"
        required
        :placeholder="$t('e.g. Hotel building')"
        :label-text="$t('Project name')"
        :error="formErrors.has('name')"
        :error-message="formErrors.get('name')"
        :input-info="$t('Upon change, contractors will be notified')"
      />
      <AddProjectFormInput
        id="project-id"
        :value="settings.project.id"
        disabled
        :label-text="$t('Project ID')"
      />
    </div>
    <div class="two-form-items two-form-items_narrow">
      <AddProjectFormInput
        id="project-address"
        v-model="address"
        required
        :placeholder="$t('e.g. Barcelona')"
        :label-text="$t('Project address')"
        :error="formErrors.has('address')"
        :error-message="formErrors.get('address')"
        :input-info="$t('Upon change, contractors will be notified')"
      />

      <div class="formcell">
        <label class="label">{{ $t('Project color') }}</label>
        <div class="project-styles-wrap">
          <ProjectColorDropdown
            ref="colorEmojiPicker"
            no-changes
            with-button
            :color="color"
            position="right"
            @color="(newColor) => (color = newColor)"
          />
        </div>
      </div>
    </div>

    <FormTextArea
      id="project-description"
      v-model="description"
      :max-length="512"
      :label-text="$t('Project description')"
      :placeholder="
        $t('Enter your project description here. It will be visible only for your company members.')
      "
      :error="formErrors.has('description')"
      :error-message="formErrors.get('description')"
    />

    <div class="two-form-items">
      <AddProjectFormInput
        id="project-customer"
        v-model="customer"
        :placeholder="$t('Enter your customer name')"
        :label-text="$t('Customer')"
        :error="formErrors.has('customer')"
        :error-message="formErrors.get('customer')"
        :input-info="$t('Upon change, contractors will be notified')"
      />
      <Select
        :label-text="$t('Project stage')"
        :items="computedProjectStates"
        :value="state"
        required
        @input="state = $event"
      />
    </div>

    <div class="two-form-items">
      <DatePickerInput
        id="price-submission"
        v-model="price_submission_date"
        :placeholder="$t('DD/MM/YY')"
        :min="minDeadlineDate"
        :max="maxDeadlineDate"
        :label-text="$t('Price submission deadline')"
        type="date"
        top
        :error="formErrors.has('price_submission_date')"
        :error-message="formErrors.get('price_submission_date')"
        :input-info="$t('Upon change, contractors will be notified')"
      />

      <DatePickerInput
        id="construction-date"
        v-model="construction_beginning_date"
        :placeholder="$t('DD/MM/YY')"
        :min="minDeadlineDate"
        :max="maxDeadlineDate"
        :label-text="$t('Construction begins on')"
        type="date"
        top
        :error="formErrors.has('construction_beginning_date')"
        :error-message="formErrors.get('construction_beginning_date')"
        :input-info="$t('Upon change, contractors will be notified')"
      />
    </div>

    <div class="modal-navigation__container">
      <div class="hr" />
      <div class="modal__navigation">
        <div />
        <div class="navigation__buttons spacex-12">
          <Button type="secondary" @click="closeModal">{{ $t('Close') }}</Button>
          <Button :loading="isLoading" action-type="submit">{{ $t('Save changes') }}</Button>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
  import AddProjectFormInput from '@/components/ui/FormInput';
  import { mapActions, mapState } from 'vuex';
  import FormTextArea from '@/components/ui/FormTextArea';
  import { currentDate, datePlus } from '@/utils/date/current-date';
  import DatePickerInput from '@/components/ui/DatePickerInput';
  import ProjectColorDropdown from '@/containers/top-menu/header/project-color/Dropdown';
  import parseError from '@/utils/formatting/parse-error';
  import capitalize from '@/utils/formatting/capitalize';
  import Select from '@/components/ui/Select';
  import Button from '@/components/ui-v2/Button';

  export default {
    name: 'ProjectSettingsForm',
    components: {
      AddProjectFormInput,
      FormTextArea,
      DatePickerInput,
      ProjectColorDropdown,
      Select,
      Button,
    },
    props: {
      settings: {
        type: Object,
        default: () => ({
          id: [String, Number],
          contract_number: String,
          client_company_name: String,
          client_company_director: String,
          user_company_name: String,
          user_company_director: String,
          project: {
            id: Number,
            type: Object,
            default: () => ({
              name: String,
              address: String,
            }),
          },
        }),
      },
      createForm: Boolean,
    },
    data() {
      return {
        name: this.settings.project.name || null,
        address: this.settings.project.address || null,
        description: this.settings.project.description || null,
        customer: this.settings.project.customer || null,
        price_submission_date: this.settings.project.price_submission_date || null,
        construction_beginning_date: this.settings.project.construction_beginning_date || null,
        color: this.settings.project.color || null,
        state: this.settings.project.state,
        isLoading: false,
        success: false,
        error: null,
        minDeadlineDate: currentDate(),
        maxDeadlineDate: datePlus(0, 0, 5),
        formErrors: new Map(),
      };
    },
    computed: {
      ...mapState('confirmModal', ['context']),
      ...mapState({
        projectStates: (state) => state.global.project_states,
        computedProjectStates() {
          const items = [];
          for (let elem in this.projectStates) {
            let text = this.projectStates?.[elem];
            items.push({ text: capitalize(text), value: elem });
          }
          return items;
        },
      }),
    },
    methods: {
      ...mapActions({
        setModalAction: 'modal/setModalAction',
        closeModal: 'modal/closeModal',
        projectUpdate: 'global/changeProjectUpdate',
        openConfirm: 'confirmModal/openModal',
        updateProject: 'project/updateProject',
        loadSidebarProjectsList: 'sidebarProjects/loadSidebarProjectsList',
        showAlert: 'alert/showAlert',
      }),
      async submit() {
        try {
          this.isLoading = true;

          await this.updateProject({
            name: this.name,
            address: this.address,
            description: this.description,
            customer: this.customer,
            price_submission_date: this.price_submission_date,
            construction_beginning_date: this.construction_beginning_date,
            color: this.color,
            state: this.state,
          });

          this.projectUpdate(true);

          if (this.createForm) this.$emit('create');

          this.$emit('update');
          this.success = true;
          this.formErrors = new Map();
          this.error = null;
          await this.loadSidebarProjectsList();
          this.showAlert({
            type: 'success',
            text: this.$t('The project has been successfully changed'),
          });
        } catch (e) {
          this.formErrors = new Map(Object.entries(e.response.data));
          this.error = this.formErrors.has('non_field_errors') ? parseError(e) : null;
        }
        this.isLoading = false;
      },
    },
  };
</script>
